import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.css";
import { Button, Form, Row, Col } from "react-bootstrap";
import { VenuesGrid } from "./VenuesGrid";
import { Base_Url } from "../../constants/constants";
import useToken from "../Login/useToken";

interface Venue {
  id: string;
  primaryType?: string;
  categories?: string[];
  // ... other venue properties
}

const VenuesView = () => {
  const [restaurants, setRestaurants] = useState<Venue[]>([]);
  const [selected, setSelected] = React.useState<readonly any[]>([]);
  const { token, setToken } = useToken();
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [neighborhoodOptions, setNeighborhoodOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedNeighborhood, setSelectedNeighborhood] = useState("");

  async function getFilterOptions() {
    try {
      const url = new URL(`${Base_Url}/venues/filterOptions`);
      const options = {
        method: "GET",
        headers: new Headers({
          Authorization: token,
          "Content-Type": "application/json",
        }),
      };

      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data) {
        setCategoryOptions(data.categories || []);
        setNeighborhoodOptions(data.boroughs || []);
      } else {
        setCategoryOptions([]);
        setNeighborhoodOptions([]);
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
      setCategoryOptions([]);
      setNeighborhoodOptions([]);
    }
  }

  async function getRestaurants() {
    const url = new URL(`${Base_Url}/venues`);
    if (searchQuery) {
      url.searchParams.append("name", searchQuery);
    }
    if (selectedCategory) {
      url.searchParams.append("category", selectedCategory);
    }
    if (selectedNeighborhood) {
      url.searchParams.append("neighborhood", selectedNeighborhood);
    }
    const options = {
      method: "GET",
      headers: new Headers({
        Authorization: token,
        "Content-Type": "application/json",
      }),
    };
    return fetch(url, options)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw response;
      })
      .then((data) => {
        console.log(data);
        if (data) {
          setRestaurants(data.restaurants);
        } else {
          throw data.errorMessage;
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }

  useEffect(() => {
    getRestaurants();
    getFilterOptions();
  }, []);

  // Add effect to refetch when filters change
  useEffect(() => {
    getRestaurants();
  }, [selectedCategory, selectedNeighborhood]);

  const removeRestaurants = async () => {
    if (!selected || selected.length == 0) return;
    return fetch(`${Base_Url}/venues`, {
      method: "DELETE",
      mode: "cors",
      credentials: "same-origin",
      headers: new Headers({
        Authorization: token,
        "Content-Type": "application/json",
      }),
      body: JSON.stringify({
        restaurants: selected.map((restaurant) => restaurant.id),
      }),
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw response;
      })
      .then((data) => {
        console.log(data);
        if (!data.success) {
          throw data.errorMessage;
        }
        setRestaurants(
          restaurants.filter((restaurant) => !selected.includes(restaurant))
        );
        return data;
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const updateVenue = async (updatedVenue: any) => {
    try {
      const response = await fetch(`${Base_Url}/venues/venue`, {
        method: "PUT",
        headers: new Headers({
          Authorization: token,
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({
          venue: updatedVenue,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // Update the local state with the new data
        setRestaurants((prevRestaurants) =>
          prevRestaurants.map((restaurant) =>
            restaurant.id === updatedVenue.id ? updatedVenue : restaurant
          )
        );
      } else {
        throw new Error(data.errorMessage || "Failed to update venues");
      }
    } catch (error) {
      console.error("Error updating venues:", error);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    getRestaurants();
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value);
  };

  const handleNeighborhoodChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedNeighborhood(e.target.value);
  };

  if (restaurants) {
    return (
      <div className="restaurantsGridWrapper">
        <Form onSubmit={handleSearch} className="mb-3">
          <Row className="align-items-center">
            <Col xs="auto">
              <Form.Group controlId="searchVenues">
                <Form.Control
                  type="text"
                  placeholder="Search venues..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs="auto">
              <Form.Group controlId="categoryFilter">
                <Form.Select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  disabled={categoryOptions.length === 0}
                >
                  <option value="">All Categories</option>
                  {categoryOptions && categoryOptions.length > 0 ? (
                    categoryOptions.map((category: any) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No categories available
                    </option>
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs="auto">
              <Form.Group controlId="neighborhoodFilter">
                <Form.Select
                  value={selectedNeighborhood}
                  onChange={handleNeighborhoodChange}
                  disabled={neighborhoodOptions.length === 0}
                >
                  <option value="">All Neighborhoods</option>
                  {neighborhoodOptions && neighborhoodOptions.length > 0 ? (
                    neighborhoodOptions.map((borough: any) => (
                      <optgroup key={borough.id} label={borough.name}>
                        {borough.neighborhoods.map((neighborhood: any) => (
                          <option
                            key={neighborhood.name}
                            value={neighborhood.name}
                          >
                            {neighborhood.name}
                          </option>
                        ))}
                      </optgroup>
                    ))
                  ) : (
                    <option value="" disabled>
                      No neighborhoods available
                    </option>
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs="auto">
              <Button variant="primary" type="submit">
                Search
              </Button>
            </Col>
          </Row>
        </Form>
        <div className="button">
          <Button variant="primary" onClick={getRestaurants}>
            Refresh
          </Button>
          <Button variant="primary" onClick={removeRestaurants}>
            Remove
          </Button>
        </div>
        <VenuesGrid
          restaurants={restaurants}
          selected={selected}
          setSelected={setSelected}
          onUpdateVenue={updateVenue}
          categoryOptions={categoryOptions}
          neighborhoodOptions={neighborhoodOptions}
        ></VenuesGrid>
      </div>
    );
  }

  return (
    <div className="restaurantsGridWrapper">
      <div className="emptyPortalResturantsGrid">Empty</div>
    </div>
  );
};

export default VenuesView;
